'use client'

import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Smartphone, Battery, Signal, Copy, Settings, Trash2, Power, RefreshCw, MessageCircle, Plus, Wifi, WifiOff, Loader2, QrCode } from 'lucide-react'
import api from '../../lib/api'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { devicesQueryOptions, DEVICES_QUERY_KEY } from '@/lib/queries'
import { getDeviceState } from '@/lib/devices'
import { Skeleton } from '../ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog'
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../ui/alert'
import { AlertTriangle } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import { AddDeviceModal } from './AddDeviceModal'
import { notify } from '@/lib/notify'

function WhatsAppReconnectDialog({ device, onDone }: { device: any; onDone: () => void }) {
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState<'idle' | 'creating' | 'waiting_qr' | 'scan' | 'authenticating' | 'ready' | 'error'>('idle')
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [qr, setQr] = useState<string | null>(null)
  const [errorMsg, setErrorMsg] = useState('')
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const queryClient = useQueryClient()

  const stopPolling = () => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null }
  }

  const startReconnect = async () => {
    setStep('creating')
    setQr(null)
    setErrorMsg('')
    try {
      const res = await api.post(`/whatsapp/devices/${device.id}/reconnect`)
      setSessionId(res.data.data.sessionId)
      setStep('waiting_qr')
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.message || 'Failed to start reconnection')
      setStep('error')
    }
  }

  useEffect(() => {
    if (!sessionId || !['waiting_qr', 'scan', 'authenticating'].includes(step)) return
    stopPolling()
    intervalRef.current = setInterval(async () => {
      try {
        if (step === 'waiting_qr' || step === 'scan') {
          const { data } = await api.get(`/whatsapp/sessions/${sessionId}/qr`)
          const { qr: newQr, sessionStatus } = data.data
          if (sessionStatus === 'ready') {
            stopPolling()
            setStep('ready')
            queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY })
            return
          }
          if (newQr && newQr !== qr) { setQr(newQr); setStep('scan') }
          if (sessionStatus === 'authenticating') setStep('authenticating')
        } else {
          const { data } = await api.get(`/whatsapp/sessions/${sessionId}/status`)
          if (data.data.sessionStatus === 'ready') {
            stopPolling()
            setStep('ready')
            queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY })
          }
        }
      } catch {}
    }, 3000)
    return stopPolling
  }, [step, sessionId])

  const handleOpen = () => {
    setOpen(true)
    startReconnect()
  }

  const handleClose = () => {
    stopPolling()
    setOpen(false)
    setStep('idle')
    setSessionId(null)
    setQr(null)
    if (step === 'ready') onDone()
  }

  return (
    <>
      <Button variant='outline' size='sm' onClick={handleOpen} className='text-xs gap-1'>
        <QrCode className='h-3.5 w-3.5' />
        Reconnect
      </Button>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>Reconnect WhatsApp</DialogTitle>
            <DialogDescription>
              Scan the QR code to relink {device.displayName || 'your WhatsApp device'}
            </DialogDescription>
          </DialogHeader>
          <div className='flex flex-col items-center gap-4 py-4'>
            {step === 'creating' && (
              <>
                <Loader2 className='h-10 w-10 animate-spin text-green-500' />
                <p className='text-sm text-muted-foreground'>Starting session…</p>
              </>
            )}
            {step === 'waiting_qr' && (
              <>
                <Loader2 className='h-8 w-8 animate-spin text-green-500' />
                <p className='text-sm text-muted-foreground'>Generating QR code…</p>
              </>
            )}
            {(step === 'scan' || step === 'authenticating') && qr && (
              <>
                <img src={qr} alt='WhatsApp QR' className='h-56 w-56 rounded-lg border' />
                {step === 'scan' && (
                  <p className='text-sm text-muted-foreground text-center max-w-xs'>
                    Open WhatsApp → <strong>Linked Devices</strong> → <strong>Link a Device</strong> → scan this QR
                  </p>
                )}
                {step === 'authenticating' && (
                  <div className='flex items-center gap-2 text-sm text-green-600'>
                    <Loader2 className='h-4 w-4 animate-spin' /> Authenticating…
                  </div>
                )}
              </>
            )}
            {step === 'ready' && (
              <>
                <Wifi className='h-12 w-12 text-green-500' />
                <p className='font-semibold'>WhatsApp reconnected!</p>
                <Button onClick={handleClose}>Done</Button>
              </>
            )}
            {step === 'error' && (
              <>
                <WifiOff className='h-10 w-10 text-red-500' />
                <p className='text-sm text-red-600'>{errorMsg}</p>
                <Button variant='outline' onClick={startReconnect}>
                  <RefreshCw className='mr-2 h-4 w-4' /> Try Again
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

function DeviceSettingsDialog({ device, onUpdate }: { device: any; onUpdate: () => void }) {
  const [open, setOpen] = useState(false)
  const [deviceName, setDeviceName] = useState(device.displayName || device.name || (device.brand && device.model ? `${device.brand} ${device.model}` : device.brand || device.model || ''))
  const [messageInterval, setMessageInterval] = useState(device.messageInterval || 0)
  const queryClient = useQueryClient()
  
  const showToast = (title: string, description?: string, variant: 'default' | 'destructive' = 'default') => {
    if (variant === 'destructive') {
      notify.error(title, description)
    } else {
      notify.success(title, description)
    }
  }

  const { mutate: updateDevice, isPending: isUpdating } = useMutation({
    mutationFn: (data: { name: string; enabled: boolean; messageInterval?: number }) =>
      api.patch(`/gateway/devices/${device.id}`, data),
    onSuccess: () => {
      showToast('Device updated successfully')
      queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY })
      setOpen(false)
      onUpdate()
    },
    onError: (error: any) => {
      showToast('Error updating device', error.response?.data?.message || error.message, 'destructive')
    },
  })

  const { mutate: deleteDevice, isPending: isDeleting } = useMutation({
    mutationFn: () => api.delete(`/gateway/devices/${device.id}`),
    onSuccess: () => {
      showToast('Device deleted successfully')
      queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY })
      setOpen(false)
      onUpdate()
    },
    onError: (error: any) => {
      showToast('Error deleting device', error.response?.data?.message || error.message, 'destructive')
    },
  })

  const handleSave = () => {
    updateDevice({
      name: deviceName,
      enabled: device.enabled,
      messageInterval: messageInterval,
    })
  }

  const handleToggleEnable = () => {
    updateDevice({
      name: deviceName,
      enabled: !device.enabled,
      messageInterval: messageInterval,
    })
  }

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this device? This action cannot be undone.')) {
      deleteDevice()
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant='ghost' size='sm'>
          <Settings className='h-4 w-4' />
        </Button>
      </DialogTrigger>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            {device.gatewayType === 'whatsapp'
              ? <MessageCircle className='h-5 w-5 text-green-500' />
              : <Smartphone className='h-5 w-5' />
            }
            Device Settings
          </DialogTitle>
          <DialogDescription>
            Manage your {device.displayName || (device.brand && device.model ? `${device.brand} ${device.model}` : device.brand || device.model || 'device')}
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-3'>
          <div className='space-y-1.5'>
            <label className='text-sm font-medium'>Device Name</label>
            <input
              type='text'
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              className='w-full px-3 py-2 border rounded-md text-sm'
              placeholder='Enter device name'
            />
          </div>

          <div className='space-y-1.5'>
            <label className='text-sm font-medium'>Message Interval (seconds)</label>
            <input
              type='number'
              min='0'
              max='300'
              value={messageInterval}
              onChange={(e) => setMessageInterval(parseInt(e.target.value) || 0)}
              className='w-full px-3 py-2 border rounded-md text-sm'
              placeholder='0'
            />
            <p className='text-xs text-muted-foreground'>
              Delay between messages (0-300s). 0 = no delay.
            </p>
          </div>

          <div className='space-y-1.5'>
            <label className='text-sm font-medium'>Device ID</label>
            <div className='flex items-center gap-2'>
              <code className='flex-1 px-2 py-1.5 bg-muted rounded-md text-xs font-mono truncate'>
                {device.id}
              </code>
              <Button
                variant='ghost'
                size='sm'
                onClick={() => navigator.clipboard.writeText(device.id)}
              >
                <Copy className='h-3.5 w-3.5' />
              </Button>
            </div>
          </div>

          {device.gatewayType === 'whatsapp' ? (
            <div className='space-y-1.5'>
              <label className='text-sm font-medium'>WhatsApp Info</label>
              <div className='grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-muted-foreground'>
                <div>Status</div>
                <div className={device.whatsappStatus === 'ready' ? 'text-green-600 font-medium' : 'text-red-600'}>
                  {device.whatsappStatus === 'ready' ? 'Connected' : device.whatsappStatus || 'Unknown'}
                </div>
                {device.phone && <><div>Phone</div><div>{device.phone}</div></>}
                {device.pushName && <><div>WA Name</div><div>{device.pushName}</div></>}
                {device.lastActive && <><div>Last Active</div><div>{new Date(device.lastActive).toLocaleString()}</div></>}
              </div>
            </div>
          ) : (
            <div className='space-y-1.5'>
              <label className='text-sm font-medium'>Device Info</label>
              <div className='grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-muted-foreground'>
                <div>Brand</div><div>{device.brand}</div>
                <div>Model</div><div>{device.model}</div>
                <div>OS</div><div>{device.os} {device.osVersion}</div>
                <div>App</div><div>{device.appVersionName}</div>
              </div>
            </div>
          )}

          <div className='flex items-center gap-2'>
            <Badge variant={device.enabled ? 'default' : 'secondary'}>
              {device.enabled ? 'Enabled' : 'Disabled'}
            </Badge>
            <span className='text-xs text-muted-foreground'>
              {device.sentSMSCount + device.receivedSMSCount} message(s)
            </span>
          </div>
        </div>

        <DialogFooter className='grid grid-cols-3 gap-2 sm:flex sm:flex-row'>
          <Button
            variant='outline'
            size='sm'
            onClick={handleToggleEnable}
            disabled={isUpdating}
            className='gap-1.5'
          >
            <Power className='h-3.5 w-3.5' />
            {isUpdating ? '...' : device.enabled ? 'Disable' : 'Enable'}
          </Button>

          <Button
            variant='destructive'
            size='sm'
            onClick={handleDelete}
            disabled={isDeleting}
            className='gap-1.5'
          >
            <Trash2 className='h-3.5 w-3.5' />
            {isDeleting ? '...' : 'Delete'}
          </Button>

          <Button
            size='sm'
            onClick={handleSave}
            disabled={isUpdating}
          >
            {isUpdating ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default function DeviceList() {
  const queryClient = useQueryClient()
  const [addDeviceOpen, setAddDeviceOpen] = useState(false)

  const showToast = (title: string, description?: string, variant?: 'default' | 'destructive') => {
    if (variant === 'destructive') {
      notify.error(title, description)
    } else {
      notify.success(title, description)
    }
  }
  
  const {
    isPending,
    error,
    data: devices = [],
    refetch
  } = useQuery<any[], Error>({
    ...devicesQueryOptions(),
    select: (data) => (Array.isArray(data) ? data : []),
  })

  const handleCopyId = (id: string) => {
    navigator.clipboard.writeText(id)
    showToast('Device ID copied to clipboard')
  }

  const handleRefreshCache = async () => {
    try {
      // Clear the cache and refetch
      await queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY })
      await refetch()
      showToast('Device list refreshed successfully')
    } catch (error) {
      showToast('Error refreshing device list', 'Please try again', 'destructive')
    }
  }

  return (
    <Card>
      <CardHeader className='pb-2'>
        <div className='flex items-center justify-between'>
          <CardTitle className='text-lg flex items-center gap-2'>
            <Smartphone className='h-5 w-5' />
            Senders
          </CardTitle>
          <div className='flex items-center gap-2'>
            <Button
              variant='ghost'
              size='sm'
              onClick={handleRefreshCache}
              disabled={isPending}
              className='flex items-center gap-2'
            >
              <RefreshCw className={`h-4 w-4 ${isPending ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button size='sm' onClick={() => setAddDeviceOpen(true)} className='flex items-center gap-2'>
              <Plus className='h-4 w-4' />
              Add a sender
            </Button>
            <AddDeviceModal open={addDeviceOpen} onOpenChange={setAddDeviceOpen} />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className='space-y-2 max-h-96 overflow-y-auto'>
          {isPending && (
            <>
              {[1, 2, 3].map((i) => (
                <Card key={i} className='border-0 shadow-none'>
                  <CardContent className='flex items-center p-3'>
                    <Skeleton className='h-6 w-6 rounded-full mr-3' />
                    <div className='flex-1'>
                      <div className='flex items-center justify-between'>
                        <Skeleton className='h-4 w-[120px]' />
                        <Skeleton className='h-4 w-[60px]' />
                      </div>
                      <div className='flex items-center space-x-2 mt-1'>
                        <Skeleton className='h-4 w-[180px]' />
                      </div>
                      <div className='flex items-center mt-1 space-x-3'>
                        <Skeleton className='h-3 w-[200px]' />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </>
          )}

          {error && (
            <div className='flex justify-center items-center h-full py-8'>
              <div className='text-center'>
                <AlertTriangle className='mx-auto h-12 w-12 text-destructive' />
                <h3 className='mt-2 text-sm font-medium'>Couldn't load your senders</h3>
                <p className='text-xs text-muted-foreground'>
                  {error.message}
                </p>
              </div>
            </div>
          )}

          {!isPending && !error && devices?.length === 0 && (
            <div className='flex justify-center items-center h-full py-8'>
              <div className='text-center'>
                <Smartphone className='mx-auto h-12 w-12 text-muted-foreground' />
                <h3 className='mt-2 text-sm font-medium'>No senders yet</h3>
                <p className='text-xs text-muted-foreground'>
                  Add an Android phone or a WhatsApp number to start sending
                </p>
              </div>
            </div>
          )}

          {devices && Array.isArray(devices) && devices.map((device: any) => (
            <Card key={device.id} className='border-0 shadow-none hover:bg-muted/50 transition-colors'>
              <CardContent className='flex items-center p-3'>
                <div className={`flex h-10 w-10 items-center justify-center rounded-full mr-3 ${device.gatewayType === 'whatsapp' ? 'bg-green-500/10' : 'bg-amber-500/10'}`}>
                  {device.gatewayType === 'whatsapp'
                    ? <MessageCircle className="h-5 w-5 text-green-500" />
                    : <Smartphone className='h-5 w-5 text-amber-500' />
                  }
                </div>
                <div className='flex-1'>
                  <div className='flex items-center justify-between'>
                    <h3 className='font-semibold text-sm'>
                      {device.displayName || (device.brand && device.model
                        ? `${device.brand} ${device.model}`
                        : device.brand || device.model || 'WhatsApp Device')}
                    </h3>
                    <div className='flex items-center gap-1'>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation()
                          try {
                            await api.patch(`/gateway/devices/${device.id}`, { enabled: !device.enabled })
                            queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY })
                          } catch {}
                        }}
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium transition-colors cursor-pointer ${
                          device.enabled
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                        title={device.enabled ? 'Click to disable' : 'Click to enable'}
                      >
                        <span className={`inline-block h-2 w-2 rounded-full ${device.enabled ? 'bg-green-500' : 'bg-gray-400'}`} />
                        {device.enabled ? 'On' : 'Off'}
                      </button>
                      <Badge
                        variant="outline"
                        className={device.gatewayType === 'whatsapp'
                          ? 'border-green-500 text-green-600 text-xs'
                          : 'border-blue-400 text-blue-600 text-xs'
                        }
                      >
                        {device.gatewayType === 'whatsapp' ? 'WhatsApp' : 'SMS'}
                      </Badge>
                    </div>
                  </div>
                  <div className='flex items-center space-x-2 mt-1'>
                    <code className='relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-xs'>
                      {device.id?.substring(0, 12) || 'No ID'}...
                    </code>
                    <Button
                      variant='ghost'
                      size='icon'
                      aria-label='Copy sender ID'
                      className='h-6 w-6'
                      onClick={() => handleCopyId(device.id)}
                    >
                      <Copy className='h-3 w-3' />
                    </Button>
                  </div>
                  {device.gatewayType === 'whatsapp' ? (
                    <div className='flex items-center mt-1 space-x-3 text-xs text-muted-foreground'>
                      <div className='flex items-center'>
                        {device.whatsappStatus === 'ready' ? (
                          <><Wifi className='h-3 w-3 mr-1 text-green-500' /><span className='text-green-600 font-medium'>Connected</span></>
                        ) : device.whatsappStatus === 'authenticating' ? (
                          <><Loader2 className='h-3 w-3 mr-1 animate-spin text-amber-500' /><span className='text-amber-600'>Connecting…</span></>
                        ) : (
                          <><WifiOff className='h-3 w-3 mr-1 text-red-500' /><span className='text-red-600'>Disconnected</span></>
                        )}
                      </div>
                      {device.phone && <div>{device.phone}</div>}
                      <div>{device.sentSMSCount + device.receivedSMSCount} message(s)</div>
                      {device.whatsappStatus !== 'ready' && (
                        <WhatsAppReconnectDialog device={device} onDone={() => queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY })} />
                      )}
                    </div>
                  ) : (
                    <div className='flex items-center mt-1 space-x-3 text-xs text-muted-foreground'>
                      <div className='flex items-center'>
                        {getDeviceState(device).state === 'online'
                          ? <><Wifi className='h-3 w-3 mr-1 text-status-delivered' /><span className='text-status-delivered font-medium'>Online</span></>
                          : <><WifiOff className='h-3 w-3 mr-1 text-status-offline' /><span className='text-gray-600'>{getDeviceState(device).detail.replace(/^./, (c) => c.toUpperCase())}</span></>}
                      </div>
                      <div>
                        {device.sentSMSCount + device.receivedSMSCount} message(s)
                      </div>
                      <div>
                        Registered: {new Date(device.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  )}
                </div>
                <DeviceSettingsDialog 
                  device={device} 
                  onUpdate={() => {
                    // Force refresh of devices query
                  }}
                />
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
